import { constant } from 'lodash';
import { type ComponentProps, memo } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { cn } from '@air/utils-tailwind';

import { type DragItem, type DragType, type DropData, type DropLocation } from '~/components/Shared/Drag/dragTypes';

export type DropRearrangeAreaProps<T extends DragItem> = {
  dropLocation: DropLocation;
  onItemRearrange: (draggedItem: T) => void;
  canDropItem?: (draggedItem: T) => boolean;
  dragTypes: DragType[];
  testId?: string;
  containerProps?: ComponentProps<'div'>;
  barProps?: ComponentProps<'div'>;
};

const collectDropData = <T extends DragItem>(
  canDropItem: DropRearrangeAreaProps<T>['canDropItem'] = constant(true),
) => {
  return (monitor: DropTargetMonitor<T>): { isOver: boolean } => {
    const isOver = monitor.isOver({ shallow: false });
    const item = monitor.getItem();

    return {
      isOver: isOver && item && canDropItem(item),
    };
  };
};

/**
 * This component is used to handle rearranging boards and workspaces in navigation
 * As well as rearranging versions in the asset modal
 * It displays horizontal line if dragged item is over it
 */
const _DropRearrangeArea = <T extends DragItem>({
  canDropItem = constant(true),
  dragTypes,
  dropLocation,
  onItemRearrange,
  containerProps = {},
  barProps = {},
  testId,
}: DropRearrangeAreaProps<T>) => {
  const onDrop = (item: T): DropData | undefined => {
    const canDrop = canDropItem(item);

    if (canDrop) {
      onItemRearrange(item);

      return {
        type: 'rearrange',
        location: dropLocation,
      };
    }
  };

  const [{ isOver }, drop] = useDrop({
    accept: dragTypes,
    collect: collectDropData(canDropItem),
    drop: onDrop,
  });

  const { className: containerClassName, ...restOfContainerProps } = containerProps;
  const { className: barPropsClassName, ...restOfBarProps } = barProps;

  return (
    <div
      data-testid={testId}
      ref={drop}
      className={cn('absolute z-20 flex h-3 w-full', containerClassName)}
      {...restOfContainerProps}
    >
      <div
        className={cn('flex h-1 w-full rounded-full', isOver && 'ads:bg-primary bg-blue-9', barPropsClassName)}
        {...restOfBarProps}
      />
    </div>
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const DropRearrangeArea = memo(_DropRearrangeArea) as typeof _DropRearrangeArea;

// @ts-expect-error - can't get displayName to work with a generic prop - https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
DropRearrangeArea.displayName = 'DropRearrangeArea';
